import { useEffect, useState } from 'react';
import useVideoContext from '../useVideoContext/useVideoContext';
import type { AgoraParticipantLike, AgoraPublicationLike } from '../../types';
import { isRemoteScreenVideoTrack } from '../../utils/agoraRemoteVideo';

function buildParticipantFromRemoteUser(user: any): AgoraParticipantLike {
  const uidRaw = user?.uid;
  const uid = typeof uidRaw === 'number' ? uidRaw : parseInt(String(uidRaw ?? ''), 10) || 0;
  return {
    identity: `User ${uid}`,
    uid,
    get audioTrack() {
      return user?.audioTrack ?? null;
    },
    get videoTrack() {
      const vt = user?.videoTrack ?? null;
      if (!vt) return null;
      return isRemoteScreenVideoTrack(vt) ? null : vt;
    },
    get screenTrack() {
      const vt = user?.videoTrack ?? null;
      if (!vt) return null;
      return isRemoteScreenVideoTrack(vt) ? vt : null;
    },
    getPublications(): AgoraPublicationLike[] {
      const out: AgoraPublicationLike[] = [];
      if (user?.audioTrack) out.push({ kind: 'audio', track: user.audioTrack, trackName: 'audio' });
      if (user?.videoTrack) {
        const isScreen = isRemoteScreenVideoTrack(user.videoTrack);
        out.push({
          kind: 'video',
          track: user.videoTrack,
          trackName: isScreen ? 'screen' : 'camera',
        });
      }
      return out;
    },
  };
}

export default function useParticipants(): AgoraParticipantLike[] {
  const { room } = useVideoContext();
  const [participants, setParticipants] = useState<AgoraParticipantLike[]>([]);

  useEffect(() => {
    if (!room) {
      setParticipants([]);
      return;
    }
    const update = () => {
      const remoteUsers = room.client.remoteUsers ?? [];
      const next = remoteUsers
        .filter(user => user && user.uid !== undefined && user.uid !== null)
        .map(user => buildParticipantFromRemoteUser(user));
      setParticipants(next);
    };
    update();
    const client = room.client;
    const onUserJoined = () => update();
    const onUserLeft = () => update();
    const onUserPublished = () => update();
    const onUserUnpublished = () => update();
    client.on('user-joined', onUserJoined);
    client.on('user-left', onUserLeft);
    client.on('user-published', onUserPublished);
    client.on('user-unpublished', onUserUnpublished);
    // Fallback reconciliation in case SDK event ordering drops a leave signal.
    const timer = window.setInterval(update, 1000);
    return () => {
      client.off('user-joined', onUserJoined);
      client.off('user-left', onUserLeft);
      client.off('user-published', onUserPublished);
      client.off('user-unpublished', onUserUnpublished);
      window.clearInterval(timer);
    };
  }, [room]);

  return participants;
}
